import { moduleSpec } from '@yourstore/domain';
import type { T } from '../../../messages';
import type { TenantReadiness } from '../../../wire';
import type { BusinessTab } from '../../router';

/**
 * **The "getting live" checklist, computed from real state** (console
 * redesign B1; docs/design/console-redesign.html, the Overview panel).
 *
 * The console used to have no idea what came next for a business. The facts
 * were all on the page, spread over fifteen cards, and the operator carried
 * the plan in their head. This file is the plan written down once: each step,
 * the fact that makes it done, who it is waiting on when it is not, and where
 * in the console it gets done.
 *
 * It is pure — facts in (`TenantReadiness`, the one read the Overview makes
 * for it), steps out — so the rule for "done" is testable without a screen,
 * and the screen (`Checklist.tsx`, `NextStep.tsx`) only draws it.
 *
 * ## Two groups
 *
 * **Getting live** is what a business needs to run on the platform at the
 * counter: what they sell, their features, the till, their hours, their book,
 * a staff login and its look, then the switch-over. **Taking bookings online**
 * is the later stage the plan names (the website, its address, the owner's
 * questionnaire, texts, the concierge). The mockup's note is explicit that the
 * second group must not clutter the first, so it is its own list, folded until
 * the business is live.
 *
 * ## Show only what applies (plan principle 6)
 *
 * A step whose feature is off is not shown: a business without checkout is
 * never asked which payments its till takes, and one without a client book is
 * never asked for its clients.
 */

export type Tick = 'done' | 'wait' | 'open';

/** Who a step is waiting on. `null` for a step that is done or cannot start yet. */
export type Who = 'operator' | 'owner' | 'platform' | null;

/** Where a step gets done: a tab of this page, or Checks & imports. */
export type Go = { kind: 'tab'; tab: BusinessTab } | { kind: 'checks' };

export interface Step {
  id: string;
  tick: Tick;
  who: Who;
  title: string;
  detail: string;
  /** The step as the "Next:" banner says it — a thing to do. */
  next: string;
  action: { label: string; go: Go } | null;
  /** A step that cannot start until the ones above it are done. */
  gated?: boolean;
}

export interface Checklist {
  live: Step[];
  online: Step[];
}

const tab = (to: BusinessTab): Go => ({ kind: 'tab', tab: to });

/** The tender words, here rather than raw tokens on screen (plan principle 2). */
function tenderWord(t: T, tender: string): string {
  switch (tender) {
    case 'cash':
      return t('overview.tender.cash');
    case 'check':
      return t('overview.tender.check');
    // Names of services, spelled the same in every language — not catalog words.
    case 'venmo':
      return 'Venmo';
    case 'paypal':
      return 'PayPal';
    case 'card':
      return t('overview.tender.card');
    case 'other':
      return t('overview.tender.other');
    default:
      return tender;
  }
}

/**
 * The features beyond the foundation. The foundation (client book, catalog,
 * calendar) and the platform's own switches are not choices an owner makes,
 * and the booking site and the partner surface belong to the online stage —
 * they are what the concierge and the website plug into, not how the business
 * runs at the counter — so a business with only those has "only the basics".
 */
const ONLINE_MODULES: ReadonlySet<string> = new Set(['booking_site', 'partner_api']);

function chosenFeatures(modules: readonly string[]): string[] {
  return modules.filter((m) => moduleSpec(m)?.kind === 'add-on' && !ONLINE_MODULES.has(m));
}

export function buildChecklist(t: T, r: TenantReadiness): Checklist {
  const on = new Set(r.modules);
  const live: Step[] = [];

  /* ---- what they sell ------------------------------------------------ */
  const unpriced = r.catalog.active - r.catalog.priced;
  if (r.catalog.active === 0) {
    live.push({
      id: 'catalog',
      tick: 'open',
      who: 'operator',
      title: t('overview.step.catalog.title'),
      detail: t('overview.step.catalog.empty'),
      next: t('overview.step.catalog.nextEmpty'),
      action: { label: t('overview.step.catalog.actionEmpty'), go: tab('catalog') },
    });
  } else if (unpriced > 0) {
    live.push({
      id: 'catalog',
      tick: 'open',
      who: 'operator',
      title: t('overview.step.catalog.title'),
      detail: t('overview.step.catalog.unpriced', {
        unpriced,
        total: r.catalog.active,
      }),
      next: t('overview.step.catalog.nextPrices'),
      action: { label: t('overview.step.catalog.actionPrices'), go: tab('catalog') },
    });
  } else {
    live.push({
      id: 'catalog',
      tick: 'done',
      who: null,
      title: t('overview.step.catalog.title'),
      detail: t('overview.step.catalog.done', {
        services: r.catalog.services,
        other: r.catalog.active - r.catalog.services,
      }),
      next: '',
      action: null,
    });
  }

  /* ---- features ------------------------------------------------------ */
  const features = chosenFeatures(r.modules);
  live.push(
    features.length > 0
      ? {
          id: 'features',
          tick: 'done',
          who: null,
          title: t('overview.step.features.title'),
          detail: features.map((m) => moduleSpec(m)?.label ?? m).join(', '),
          next: '',
          action: null,
        }
      : {
          id: 'features',
          tick: 'open',
          who: 'operator',
          title: t('overview.step.features.title'),
          detail: t('overview.step.features.open'),
          next: t('overview.step.features.next'),
          action: { label: t('overview.step.features.action'), go: tab('setup') },
        },
  );

  /* ---- the till: only for a business with checkout ------------------- */
  if (on.has('sales')) {
    live.push(
      r.till_tenders !== null
        ? {
            id: 'tenders',
            tick: 'done',
            who: null,
            title: t('overview.step.tenders.title'),
            detail: r.till_tenders.map((x) => tenderWord(t, x)).join(' · '),
            next: '',
            action: null,
          }
        : {
            id: 'tenders',
            tick: 'open',
            who: 'operator',
            title: t('overview.step.tenders.title'),
            detail: t('overview.step.tenders.open'),
            next: t('overview.step.tenders.next'),
            action: { label: t('overview.step.tenders.action'), go: tab('setup') },
          },
    );
  }

  /* ---- hours --------------------------------------------------------- */
  // The console cannot tell a real week from a placeholder one — the document
  // has no marker — so this is "set" or "not set" and says no more than that.
  live.push(
    r.open_days > 0
      ? {
          id: 'hours',
          tick: 'done',
          who: null,
          title: t('overview.step.hours.title'),
          detail: t('overview.step.hours.done', { days: r.open_days }),
          next: '',
          action: null,
        }
      : {
          id: 'hours',
          tick: 'open',
          who: 'operator',
          title: t('overview.step.hours.title'),
          detail: t('overview.step.hours.open'),
          next: t('overview.step.hours.next'),
          // Hours come in from the owner's answers, which are applied on the
          // Website tab — the console has no other place that writes them.
          action: { label: t('overview.step.hours.action'), go: tab('site') },
        },
  );

  /* ---- their book: only for a business with a client book ------------ */
  if (on.has('clients')) {
    live.push(
      r.clients > 0
        ? {
            id: 'clients',
            tick: 'done',
            who: null,
            title: t('overview.step.clients.title'),
            detail: t('overview.step.clients.done', { count: r.clients }),
            next: '',
            action: null,
          }
        : {
            id: 'clients',
            tick: 'wait',
            who: 'owner',
            title: t('overview.step.clients.title'),
            detail: t('overview.step.clients.wait'),
            next: t('overview.step.clients.next'),
            action: { label: t('overview.step.clients.action'), go: { kind: 'checks' } },
          },
    );
  }

  /* ---- staff login and its look -------------------------------------- */
  live.push(
    r.staff_seats > 0
      ? {
          id: 'staff',
          tick: 'done',
          who: null,
          title: t('overview.step.staff.title'),
          detail: t('overview.step.staff.done', { count: r.staff_seats }),
          next: '',
          action: null,
        }
      : {
          id: 'staff',
          tick: 'open',
          who: 'operator',
          title: t('overview.step.staff.title'),
          detail: t('overview.step.staff.open'),
          next: t('overview.step.staff.next'),
          // Invites go out from Setup's Who can sign in card (the console does
          // both halves the CLI used to split across a laptop and a job).
          action: { label: t('overview.step.staff.action'), go: tab('setup') },
        },
  );
  live.push(
    r.staff_look
      ? {
          id: 'look',
          tick: 'done',
          who: null,
          title: t('overview.step.look.title'),
          detail: t('overview.step.look.done'),
          next: '',
          action: null,
        }
      : {
          id: 'look',
          tick: 'open',
          who: 'operator',
          title: t('overview.step.look.title'),
          detail: t('overview.step.look.open'),
          next: t('overview.step.look.next'),
          action: { label: t('overview.step.look.action'), go: tab('setup') },
        },
  );

  /* ---- the switch-over: gated on everything above -------------------- */
  const above = live.every((s) => s.tick === 'done');
  if (r.status === 'live') {
    live.push({
      id: 'switch',
      tick: 'done',
      who: null,
      title: t('overview.step.switch.title'),
      detail: t('overview.step.switch.done'),
      next: '',
      action: null,
    });
  } else {
    live.push({
      id: 'switch',
      tick: 'open',
      who: above ? 'operator' : null,
      title: t('overview.step.switch.title'),
      detail: above ? t('overview.step.switch.ready') : t('overview.step.switch.gated'),
      next: t('overview.step.switch.next'),
      action: null,
      gated: !above,
    });
  }

  /* ---- taking bookings online ---------------------------------------- */
  const online: Step[] = [];

  const intake = r.intake;
  if (intake === null) {
    online.push({
      id: 'questionnaire',
      tick: 'open',
      who: 'operator',
      title: t('overview.step.questionnaire.title'),
      detail: t('overview.step.questionnaire.none'),
      next: t('overview.step.questionnaire.nextSend'),
      action: { label: t('overview.step.questionnaire.actionSend'), go: tab('site') },
    });
  } else if (intake.status === 'closed') {
    online.push({
      id: 'questionnaire',
      tick: 'done',
      who: null,
      title: t('overview.step.questionnaire.title'),
      detail: t('overview.step.questionnaire.closed'),
      next: '',
      action: null,
    });
  } else if (intake.status === 'submitted') {
    online.push({
      id: 'questionnaire',
      tick: 'open',
      who: 'operator',
      title: t('overview.step.questionnaire.title'),
      detail: t('overview.step.questionnaire.answered'),
      next: t('overview.step.questionnaire.nextReview'),
      action: { label: t('overview.step.questionnaire.actionReview'), go: tab('site') },
    });
  } else if (intake.expired) {
    online.push({
      id: 'questionnaire',
      tick: 'open',
      who: 'operator',
      title: t('overview.step.questionnaire.title'),
      detail: t('overview.step.questionnaire.expired'),
      next: t('overview.step.questionnaire.nextExtend'),
      action: { label: t('overview.step.questionnaire.actionExtend'), go: tab('site') },
    });
  } else {
    online.push({
      id: 'questionnaire',
      tick: 'wait',
      who: 'owner',
      title: t('overview.step.questionnaire.title'),
      detail: t('overview.step.questionnaire.sent'),
      next: t('overview.step.questionnaire.nextWait'),
      action: { label: t('overview.step.questionnaire.actionOpen'), go: tab('site') },
    });
  }

  online.push(
    r.site_version > 0
      ? {
          id: 'site',
          tick: 'done',
          who: null,
          title: t('overview.step.site.title'),
          detail: t('overview.step.site.done'),
          next: '',
          action: null,
        }
      : {
          id: 'site',
          tick: 'open',
          who: 'operator',
          title: t('overview.step.site.title'),
          detail: t('overview.step.site.open'),
          next: t('overview.step.site.next'),
          action: { label: t('overview.step.site.action'), go: tab('site') },
        },
  );

  if (r.domains.custom_live > 0) {
    online.push({
      id: 'address',
      tick: 'done',
      who: null,
      title: t('overview.step.address.title'),
      detail: t('overview.step.address.done'),
      next: '',
      action: null,
    });
  } else if (r.domains.custom > 0) {
    online.push({
      id: 'address',
      tick: 'wait',
      who: 'platform',
      title: t('overview.step.address.title'),
      detail: t('overview.step.address.wait'),
      next: t('overview.step.address.nextCheck'),
      action: { label: t('overview.step.address.actionCheck'), go: tab('site') },
    });
  } else {
    online.push({
      id: 'address',
      tick: 'open',
      who: 'operator',
      title: t('overview.step.address.title'),
      detail: t('overview.step.address.open'),
      next: t('overview.step.address.next'),
      action: { label: t('overview.step.address.action'), go: tab('site') },
    });
  }

  const textsDone = r.texts.owner === 'partner' || r.texts.reminders;
  online.push(
    textsDone
      ? {
          id: 'texts',
          tick: 'done',
          who: null,
          title: t('overview.step.texts.title'),
          detail:
            r.texts.owner === 'partner'
              ? t('overview.step.texts.partner')
              : t('overview.step.texts.done'),
          next: '',
          action: null,
        }
      : {
          id: 'texts',
          tick: 'open',
          who: 'operator',
          title: t('overview.step.texts.title'),
          detail: t('overview.step.texts.open'),
          next: t('overview.step.texts.next'),
          // Reminders are switched on from the operator tool; no console card
          // writes them yet, and the step says so instead of a dead button.
          action: null,
        },
  );

  online.push(
    r.concierge
      ? {
          id: 'concierge',
          tick: 'done',
          who: null,
          title: t('overview.step.concierge.title'),
          detail: t('overview.step.concierge.done'),
          next: '',
          action: null,
        }
      : {
          id: 'concierge',
          tick: 'open',
          who: 'operator',
          title: t('overview.step.concierge.title'),
          detail: t('overview.step.concierge.open'),
          next: t('overview.step.concierge.next'),
          action: { label: t('overview.step.concierge.action'), go: tab('connections') },
        },
  );

  return { live, online };
}

/**
 * **The single next step** — the first step not done that is somebody's to do
 * (the operator's or the owner's), in the checklist's own order.
 *
 * Getting live comes first; the online group is only consulted once the
 * business is live, because before then its steps are not the next thing even
 * when they are open. A step waiting on the platform (an address whose
 * certificate is being issued) is nobody's to do, and a gated step (the
 * switch-over, before the rest is done) cannot be done yet; both are skipped.
 * `null` when there is nothing left — the banner then does not draw.
 */
export function nextStep(list: Checklist, status: string): Step | null {
  const doable = (s: Step): boolean =>
    s.tick !== 'done' && (s.who === 'operator' || s.who === 'owner') && s.gated !== true;
  const first = list.live.find(doable);
  if (first !== undefined) return first;
  if (status !== 'live') return null;
  return list.online.find(doable) ?? null;
}

export function doneCount(steps: readonly Step[]): number {
  return steps.filter((s) => s.tick === 'done').length;
}
